import { Injectable } from '@angular/core';
import { Action, State, StateContext, Store } from '@ngxs/store';
import _ from 'lodash';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { QueueSelectionPurpose } from 'src/app/samwebDescription/samwin/QueueSelectionPurpose';
import { QueueTypeEnum } from 'src/app/samwebDescription/samwin/QueueTypeEnum';
import { RouteTableTypeEnum } from 'src/app/samwebDescription/samwin/RouteTableTypeEnum';
import { QueueShortInfoRemoteDto, UsersService } from 'src/generated/swagger';
import {
   CompleteQueueSelectionProcess,
   EndQueueSelectionProcess,
   QueueSelectionSearchTextChanged,
   QueueSelectionSelectQueue,
   StartQueueSelectionProcess,
} from './queue-selection-dialog.actions';

export interface QueueSelectionDialogStateModel {
   purpose: QueueSelectionPurpose;
   searchText: string;
   contextData: any;
   selectableQueues: QueueShortInfoRemoteDto[];
   selectedQueue: QueueShortInfoRemoteDto;
}

@State<QueueSelectionDialogStateModel>({
   name: 'queueSelectionDialog',
   defaults: {
      purpose: null,
      searchText: '',
      contextData: null,
      selectableQueues: null,
      selectedQueue: null,
   },
})
@Injectable()
export class QueueSelectionDialogState {
   constructor(private store: Store, private usersService: UsersService) {}

   @Action(StartQueueSelectionProcess, { cancelUncompleted: true })
   public StartQueueSelectionProcess(ctx: StateContext<QueueSelectionDialogStateModel>, action: StartQueueSelectionProcess) {
      if (action.purpose.requireCall && _.isNil(action.contextData)) {
         console.warn(`Call is required for purpose ${action.purpose} but not given`);
         this.store.dispatch(new EndQueueSelectionProcess());
         return;
      }

      ctx.patchState({
         purpose: action.purpose,
         contextData: action.contextData,
         selectableQueues: null,
      });

      let queues = action.purpose.queueSelectionRequest(this.store, this.usersService, action.contextData);

      if (!_.isNil(action.currentSkillId)) {
         queues = queues.pipe(
            map((response: QueueShortInfoRemoteDto[]) =>
               _(response)
                  .filter((x) => x.id !== action.currentSkillId)
                  .value()
            )
         );
      }
      return queues
         .pipe(
            tap((response: QueueShortInfoRemoteDto[]) => {
               const newSelectableQueues: QueueShortInfoRemoteDto[] = response ?? [];
               // reselect previous item or fallback to first available
               let newSelectedQueue = ctx.getState().selectedQueue;
               if (!_.isNil(newSelectedQueue)) {
                  newSelectedQueue = newSelectableQueues.find((q) => q.id === newSelectedQueue.id);
               }
               if (_.isNil(newSelectedQueue)) {
                  newSelectedQueue = _(newSelectableQueues).first();
               }
               ctx.patchState({
                  selectableQueues: newSelectableQueues,
                  selectedQueue: newSelectedQueue,
               });
            })
         )
         .subscribe();
   }

   @Action(QueueSelectionSearchTextChanged)
   public QueueSelectionSearchTextChanged(ctx: StateContext<QueueSelectionDialogStateModel>, action: QueueSelectionSearchTextChanged) {
      ctx.patchState({
         searchText: action.searchText,
      });
   }

   @Action(QueueSelectionSelectQueue)
   public QueueSelectionSelectQueue(ctx: StateContext<QueueSelectionDialogStateModel>, action: QueueSelectionSelectQueue) {
      console.log(`Selected queue ${action.queue}`);
      ctx.patchState({
         selectedQueue: action.queue,
      });
   }

   @Action(CompleteQueueSelectionProcess, { cancelUncompleted: true })
   public CompleteQueueSelectionProcess(ctx: StateContext<QueueSelectionDialogStateModel>) {
      const contextData = ctx.getState().contextData;
      const selectedQueue = ctx.getState().selectedQueue;

      this.store.dispatch(new EndQueueSelectionProcess());

      if (_.isNil(selectedQueue)) {
         console.log('Cannot complete queue selection process. No queue selected.');
         return;
      }

      console.log(`Completing call selection with queue ${selectedQueue.id}`);
      return ctx.getState().purpose.completeQueueSelection(this.store, selectedQueue, contextData);
   }

   @Action(EndQueueSelectionProcess)
   public EndQueueSelectionProcess(ctx: StateContext<QueueSelectionDialogStateModel>, action: EndQueueSelectionProcess) {
      console.log(`End queue selection`);
      ctx.patchState({
         purpose: null,
         contextData: null,
         selectableQueues: null,
      });
   }
}
